import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { LanguageSwitcher } from '../ui/LanguageSwitcher';
import { useLanguageLink } from '../../hooks/useLanguageLink';

interface PublicLayoutProps {
  children: ReactNode;
}

export default function PublicLayout({ children }: PublicLayoutProps) {
  const { t } = useTranslation(['marketing', 'common']);
  const { isAuthenticated } = useAuthStore();
  const currentYear = new Date().getFullYear();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { createLink } = useLanguageLink();

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Header */}
      <header className="public-header">
        <div className="container-marketing">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to={createLink('')} className="flex items-center space-x-3">
              <img src="/AxetraLogo.webp" alt="Axetra" className="h-8 md:h-10" />
              <span className="text-2xl md:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-800">
                Axetra
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <Link
                to={`${createLink('')}#features`}
                className="text-sm font-medium text-gray-700 hover:text-primary-600 transition-colors"
              >
                {t('footer.product_features', { ns: 'marketing' })}
              </Link>
              <Link
                to={createLink('pricing')}
                className="text-sm font-medium text-gray-700 hover:text-primary-600 transition-colors"
              >
                {t('footer.product_pricing', { ns: 'marketing' })}
              </Link>
              <Link
                to={createLink('blog')}
                className="text-sm font-medium text-gray-700 hover:text-primary-600 transition-colors"
              >
                Blog
              </Link>
              <Link
                to={createLink('faq')}
                className="text-sm font-medium text-gray-700 hover:text-primary-600 transition-colors"
              >
                {t('footer.resources_faq', { ns: 'marketing' })}
              </Link>
            </nav>

            {/* Right Side Actions */}
            <div className="flex items-center space-x-4">
              <LanguageSwitcher />

              {isAuthenticated ? (
                <Link
                  to="/dashboard"
                  className="btn btn-primary hidden md:inline-flex"
                >
                  {t('navigation.dashboard', { ns: 'common' })}
                </Link>
              ) : (
                <>
                  <Link
                    to={createLink('login')}
                    className="hidden md:inline-block text-sm font-medium text-gray-700 hover:text-primary-600 transition-colors"
                  >
                    {t('auth.login', { ns: 'common' })}
                  </Link>
                  <Link
                    to={createLink('register')}
                    className="btn btn-primary hidden md:inline-flex"
                  >
                    {t('hero.cta_primary', { ns: 'marketing' })}
                  </Link>
                </>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 text-gray-700 hover:text-primary-600 transition-colors"
                aria-label="Toggle mobile menu"
              >
                {isMobileMenuOpen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200 animate-fade-in">
              <nav className="flex flex-col space-y-3">
                <Link
                  to={`${createLink('')}#features`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                >
                  {t('footer.product_features', { ns: 'marketing' })}
                </Link>
                <Link
                  to={createLink('pricing')}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                >
                  {t('footer.product_pricing', { ns: 'marketing' })}
                </Link>
                <Link
                  to={createLink('blog')}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                >
                  Blog
                </Link>
                <Link
                  to={createLink('faq')}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                >
                  {t('footer.resources_faq', { ns: 'marketing' })}
                </Link>
                {isAuthenticated ? (
                  <Link
                    to="/dashboard"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="mx-4 btn btn-primary text-center"
                  >
                    {t('navigation.dashboard', { ns: 'common' })}
                  </Link>
                ) : (
                  <>
                    <Link
                      to={createLink('login')}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                    >
                      {t('auth.login', { ns: 'common' })}
                    </Link>
                    <Link
                      to={createLink('register')}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="mx-4 btn btn-primary text-center"
                    >
                      {t('hero.cta_primary', { ns: 'marketing' })}
                    </Link>
                  </>
                )}
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300">
        <div className="container-marketing py-12 md:py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {/* Product Column */}
            <div>
              <h3 className="text-white font-semibold mb-4">
                {t('footer.product_title', { ns: 'marketing' })}
              </h3>
              <ul className="space-y-3">
                <li>
                  <Link
                    to={`${createLink('')}#features`}
                    className="text-sm hover:text-white transition-colors"
                  >
                    {t('footer.product_features', { ns: 'marketing' })}
                  </Link>
                </li>
                <li>
                  <Link
                    to={createLink('pricing')}
                    className="text-sm hover:text-white transition-colors"
                  >
                    {t('footer.product_pricing', { ns: 'marketing' })}
                  </Link>
                </li>
                <li>
                  <Link
                    to={`${createLink('')}#how-it-works`}
                    className="text-sm hover:text-white transition-colors"
                  >
                    {t('footer.product_how_it_works', { ns: 'marketing' })}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Resources Column */}
            <div>
              <h3 className="text-white font-semibold mb-4">
                {t('footer.resources_title', { ns: 'marketing' })}
              </h3>
              <ul className="space-y-3">
                <li>
                  <Link
                    to={createLink('faq')}
                    className="text-sm hover:text-white transition-colors"
                  >
                    {t('footer.resources_faq', { ns: 'marketing' })}
                  </Link>
                </li>
                <li>
                  <Link
                    to={createLink('blog')}
                    className="text-sm hover:text-white transition-colors"
                  >
                    Blog
                  </Link>
                </li>
                <li>
                  <Link
                    to={createLink('vs/adspect')}
                    className="text-sm hover:text-white transition-colors"
                  >
                    Comparisons
                  </Link>
                </li>
                <li>
                  <Link
                    to={createLink('contact')}
                    className="text-sm hover:text-white transition-colors"
                  >
                    {t('footer.resources_support', { ns: 'marketing' })}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Company Column */}
            <div>
              <h3 className="text-white font-semibold mb-4">
                {t('footer.company_title', { ns: 'marketing' })}
              </h3>
              <ul className="space-y-3">
                <li>
                  <Link
                    to={createLink('about')}
                    className="text-sm hover:text-white transition-colors"
                  >
                    {t('footer.company_about', { ns: 'marketing' })}
                  </Link>
                </li>
                <li>
                  <Link
                    to={createLink('contact')}
                    className="text-sm hover:text-white transition-colors"
                  >
                    {t('footer.company_contact', { ns: 'marketing' })}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Legal Column */}
            <div>
              <h3 className="text-white font-semibold mb-4">
                {t('footer.legal_title', { ns: 'marketing' })}
              </h3>
              <ul className="space-y-3">
                <li>
                  <Link
                    to={createLink('privacy')}
                    className="text-sm hover:text-white transition-colors"
                  >
                    {t('footer.legal_privacy', { ns: 'marketing' })}
                  </Link>
                </li>
                <li>
                  <Link
                    to={createLink('terms')}
                    className="text-sm hover:text-white transition-colors"
                  >
                    {t('footer.legal_terms', { ns: 'marketing' })}
                  </Link>
                </li>
                <li>
                  <Link
                    to={createLink('cookies')}
                    className="text-sm hover:text-white transition-colors"
                  >
                    {t('footer.legal_cookies', { ns: 'marketing' })}
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-12 pt-8 border-t border-gray-800">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-sm text-gray-400">
                {t('footer.copyright', { year: currentYear, ns: 'marketing' })}
              </p>
              <div className="flex items-center space-x-2 mt-4 md:mt-0">
                <Link to={createLink('')}>
                  <img src="/AxetraLogo.webp" alt="Axetra" className="h-6" />
                </Link>
                <span className="text-xs text-gray-500">
                  {t('footer.tagline', { ns: 'marketing' })}
                </span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
